import moment from 'moment';
import dayjs from 'dayjs';
import { getLocalizedShortDate } from '../_locale/dateLocales';
import { COUNTRY_CODES } from './constants';

const arrMonth = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const days = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const licenseMap = {
  enterprise: 'Enterprise Plan',
  business: 'Business Plan',
  free: 'Free Plan',
  team: 'Team Plan',
};

export const getDate = (UTF) => {
  if (!UTF) return;
  const date = moment(UTF);
  return date.format('DD/MM/YYYY');
};

export const getDateForCard = (UTF) => {
  if (!UTF) return;
  const dateObj = moment(UTF);
  const currentYear = moment().year();
  const day = dateObj.date();
  const month = dateObj.month();
  const year = dateObj.year();

  const dateText = getDateText(UTF, dateObj);
  if (['Today', 'Tomorrow', 'Yesterday', ...days].includes(dateText)) {
    return dateText;
  }

  const suffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const formattedDate = `${arrMonth[month].substring(0, 3)} ${day}${suffix(day)}`;
  return year === currentYear ? formattedDate : `${formattedDate}, ${year}`;
};

export const getDateWithoutChangingTZ = (UTF) => {
  if (!UTF) return;
  return moment(UTF).format('DD/MM/YYYY');
};

export const dayMonth = (UTF) => {
  const date = moment(UTF);
  const day = date.date();
  const month = arrMonth[date.month()].substring(0, 3);
  return `${day} ${month}`;
};

export const getToday = () => moment().format('DD/MM/YYYY');

export const getYesterday = () =>
  moment().subtract(1, 'day').format('DD/MM/YYYY');

export const getTomorrow = () => moment().add(1, 'day').format('DD/MM/YYYY');

export const getDateColor = (UTF, isCompleted) => {
  if (!UTF) {
    return 'grey';
  }
  if (isCompleted) {
    return 'grey';
  }
  const today = dayjs().startOf('day'); // Start of today's date
  const givenDate = dayjs(UTF);

  if (givenDate.isSame(today, 'day')) {
    return 'orange';
  } else if (givenDate.isBefore(today, 'day')) {
    return 'red';
  } else {
    return 'grey';
  }
};

export const isItThisWeek = (date) => {
  const inputDate = moment(date, 'DD/MM/YYYY');
  const startOfWeek = moment().startOf('week');
  const endOfWeek = moment().endOf('week');
  return inputDate.isBetween(startOfWeek, endOfWeek, null, '[]');
};

export const isItLastWeek = (date) => {
  const inputDate = moment(date, 'DD/MM/YYYY');
  const startOfLastWeek = moment().subtract(1, 'week').startOf('week');
  const endOfLastWeek = moment().subtract(1, 'week').endOf('week');
  return inputDate.isBetween(startOfLastWeek, endOfLastWeek, null, '[]');
};

export const getDateText = (date, dateObj) => {
  if (!date) return 'No due date';

  const formattedDate = getDate(date);
  const today = getToday();
  const tomorrow = getTomorrow();
  const yesterday = getYesterday();

  if (formattedDate === today) return 'Today';
  if (formattedDate === tomorrow) return 'Tomorrow';
  if (formattedDate === yesterday) return 'Yesterday';
  if (isItThisWeek(formattedDate)) return days[moment(date).day()];
  return formattedDate;
};

export const getDateTextLocalized = (dateString, intl) => {
  if (!dateString) return 'No due date';

  const shortDate = getDate(dateString);

  const momentDate = moment(dateString, 'DD/MM/YYYY').startOf('day');
  const now = moment().startOf('day');

  if (momentDate.isSame(now, 'day'))
    return intl.formatMessage({ id: 'TODAY', defaultMessage: 'Today' });
  if (momentDate.diff(now, 'days') === 1)
    return intl.formatMessage({ id: 'TOMORROW', defaultMessage: 'Tomorrow' });
  if (momentDate.diff(now, 'days') === -1)
    return intl.formatMessage({ id: 'YESTERDAY', defaultMessage: 'Yesterday' });

  const dateObject = new Date(dateString);

  if (isItLastWeek(shortDate))
    return dateObject.toLocaleDateString(intl.locale, { weekday: 'long' });

  return dateObject.toLocaleDateString(intl.locale, {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
  });
};

export const getDateTextLongFormat = (dateParam, intl) => {
  if (!dateParam) return 'No date specified';

  const date = dateParam instanceof Date ? dateParam : new Date(dateParam);

  const shortDate = getDate(date);

  const momentDate = moment(date, 'DD/MM/YYYY').startOf('day');
  const year = momentDate.year();
  const now = moment().startOf('day');

  if (momentDate.isSame(now, 'day'))
    return intl.formatMessage({ id: 'TODAY', defaultMessage: 'Today' });
  if (momentDate.diff(now, 'days') === 1)
    return intl.formatMessage({ id: 'TOMORROW', defaultMessage: 'Tomorrow' });
  if (momentDate.diff(now, 'days') === -1)
    return intl.formatMessage({ id: 'YESTERDAY', defaultMessage: 'Yesterday' });

  if (isItLastWeek(shortDate))
    return date.toLocaleDateString(intl.locale, { weekday: 'long' });

  const isSameYear = momentDate.isSame(now, 'year');
  switch (intl.locale) {
    case 'en-US': {
      const localizedShortDate = getLocalizedShortDate(date, intl, true);
      return isSameYear ? localizedShortDate : `${localizedShortDate}, ${year}`;
    }
    default: {
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        year: isSameYear ? undefined : 'numeric',
      });
    }
  }
};

export const getTodayLongFormat = (intl) => {
  const today = new Date();

  return today.toLocaleDateString(intl.locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

export const getUserLicense = (user, organizationId) => {
  const userOrg = user?.organizations?.find(
    (o) => o?._id?.toString() == organizationId?.toString(),
  );
  if (!userOrg) {
    // User is not part of selected org
    return 'Free Plan';
  }

  return licenseMap[userOrg?.license?.name];
};

export const getOrgLicense = (user, organizationId) => {
  const userOrg = user?.organizations?.find(
    (o) => o?._id?.toString() == organizationId?.toString(),
  );
  if (!userOrg) {
    // User is not part of selected org
    return 'Free Plan';
  }

  return licenseMap[userOrg?.orgLicense];
};

export const getLicenseName = (license) => licenseMap[license];

export function getMyOrgs(user) {
  return user?.organizations?.filter(org => org?.role == 'admin' || org?.role == 'superAdmin')
}

export const stripHtml = (html) => {
  if (!html) return '';

  let cleaned = html.replace(/<img[^>]*>/gi, '');
  cleaned = cleaned.replace(/<[^>]*>/g, '');

  const textarea = document.createElement('textarea');
  textarea.innerHTML = cleaned;
  cleaned = textarea.value;

  return cleaned.replace(/\s+/g, ' ').trim();
};

export const isImageOnlyMessage = (html) => {
  if (!html) return false;

  let withoutImages = html.replace(/<img[^>]*>/gi, '');
  withoutImages = withoutImages.replace(/<[^>]*>/g, '');

  const textarea = document.createElement('textarea');
  textarea.innerHTML = withoutImages;
  const cleanedText = textarea.value.trim();

  return cleanedText === '' || cleanedText.length === 0;
};

export const getSearchMessages = (messages) => {
  if (Array.isArray(messages)) {
    return messages.filter(message => !isImageOnlyMessage(message?.messageDescription));
  }

  if (typeof messages === 'object' && messages !== null) {
    return Object.values(messages).filter(message => !isImageOnlyMessage(message?.messageDescription));
  }

  return [];
};

export const getSearchContext = (text, searchTerm, maxLength = 150) => {
  if (!searchTerm || !text) return text.substring(0, maxLength);

  const lowerText = text.toLowerCase();
  const lowerSearchTerm = searchTerm.toLowerCase();
  const searchIndex = lowerText.indexOf(lowerSearchTerm);

  if (searchIndex === -1) {
    return text.substring(0, maxLength);
  }

  const contextStart = Math.max(0, searchIndex - Math.floor(maxLength / 3));
  let contextText = text.substring(contextStart, contextStart + maxLength);

  if (contextStart > 0) {
    contextText = '...' + contextText;
  }

  return contextText;
};

export const getTime = (isoTime) => {
  return moment(isoTime).local().format('h:mm A');
}

export let closeContextMenu = null;

export function registerContextMenuCloser(closerFn) {
  closeContextMenu = closerFn;
}
export const isMac = navigator.userAgent.toLowerCase().indexOf('mac') > -1;

function hexToHsl(hex) {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
        hex = hex.split("").map((c) => c + c).join("");
    }
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
    s /= 100;
    l /= 100;
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) =>
        l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return `#${[f(0), f(8), f(4)]
        .map((x) => Math.round(x * 255).toString(16).padStart(2, "0"))
        .join("")}`;
}

export function generateShades(baseHex, count) {
  // 1. Convert the base Hex to HSL
  const [h, s] = hexToHsl(baseHex);

  // 2. Define the target lightness range (L: 15% to 85%)
  const START_L = 20; // Darkest shade
  const END_L = 85;   // Lightest shade
  const RANGE = END_L - START_L;

  // Ensure count is at least 1
  count = Math.max(1, count); 
  
  // 3. Calculate the step size for even distribution
  // Subtract 1 from count to ensure both START_L and END_L are included if count > 1
  const step = count > 1 ? RANGE / (count - 1) : 0; 
  
  const shades = [];

  for (let i = 0; i < count; i++) {
      // Calculate the new lightness value
      const lightness = START_L + (i * step);
      
      // 4. Convert back to Hex and add to the array
      shades.push(hslToHex(h, s, lightness));
  }
  
  return shades;
}

export function getCountryFlag(phoneValue) {
  if (!phoneValue) return null;

  const phoneNumber = phoneValue.replace(/\s+/g, '');
  if (!phoneNumber.startsWith('+')) return null;

  for (let len = 4; len >= 1; len--) {
    const code = phoneNumber.substring(0, len + 1);
    if (COUNTRY_CODES[code]) {
      return COUNTRY_CODES[code];
    }
  } 
  return null;
}
